import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Package, RefreshCcw, MessageSquare, ShoppingBag, LogOut } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const NAV = [
  { to: "/owner-cabinet/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/owner-cabinet/products", label: "Products", icon: Package },
  { to: "/owner-cabinet/ebay", label: "eBay Sync", icon: RefreshCcw },
  { to: "/owner-cabinet/inquiries", label: "Inquiries", icon: MessageSquare },
  { to: "/owner-cabinet/orders", label: "Orders", icon: ShoppingBag },
];

export default function OwnerLayout() {
  const [admin, setAdmin] = useState(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/admin/me").then(({ data }) => setAdmin(data)).catch(() => setAdmin(null));
  }, []);

  useEffect(() => {
    if (admin === null) navigate("/owner-cabinet/login", { replace: true });
  }, [admin, navigate]);

  const logout = async () => {
    try { await api.post("/auth/admin/logout"); } catch {}
    navigate("/owner-cabinet/login");
  };

  if (admin === undefined) return <div className="min-h-screen bg-[#0B0F19] p-10"><Skeleton className="h-20" /></div>;
  if (!admin) return null;

  return (
    <div data-testid="owner-layout" className="min-h-screen bg-[#0B0F19] text-slate-100 flex">
      <aside className="w-64 shrink-0 border-r border-white/10 bg-[#0B0F19] p-5 flex flex-col">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-[#0055FF] flex items-center justify-center"><span className="font-display text-white">IC</span></div>
          <div>
            <div className="font-display text-lg text-white leading-none">Owner Cabinet</div>
            <div className="text-[9px] font-mono uppercase tracking-[0.3em] text-slate-400">InterCorp Industries</div>
          </div>
        </div>
        <nav className="space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`owner-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-md text-sm ${isActive ? "bg-[#0055FF]/15 text-[#0055FF]" : "text-slate-300 hover:bg-white/5"}`}
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="text-xs text-slate-400">{admin.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{admin.role}</div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="owner-logout" className="mt-3 w-full border-white/10 bg-white/5 text-white hover:bg-white/10 gap-1.5"><LogOut className="w-3.5 h-3.5" /> Sign out</Button>
        </div>
      </aside>
      <main className="flex-1 p-8 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
